import Activities from 'app/Private/Activities';
import CreditNotesListing from 'app/Private/CreditNotes/CreditNoteListing';
import { Dashboard } from 'app/Private/Dashboard';
import { GroupClients } from 'app/Private/GroupClients/GroupClients';
import { GroupCompanies } from 'app/Private/GroupCompanies/GroupCompanies';
import { GroupMembers } from 'app/Private/GroupMembers/GroupMembers';
import { GroupOverview } from 'app/Private/GroupOverview/GroupOverview';
import { GroupView } from 'app/Private/GroupView/GroupView';
import Invoice from 'app/Private/Invoices/Invoice';
import InvoicesListing from 'app/Private/Invoices/Listing';
import InvoicesToHandle from 'app/Private/Invoices/ToHandle';
import TransactionHistory from 'app/Private/Transactions/History';
import Reconciliation from 'app/Private/Transactions/Reconciliation';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { upperFirst } from 'lodash-es';
import { AtRisk } from 'modules/AtRisk';
import { Import } from 'modules/Import';
import { Kanban } from 'modules/Kanban';
import { MailPage } from 'modules/Mails/MailPage';
import { MailDetail } from 'modules/Mails/pages/MailDetail';
import { ProductsIndex as ProductsListing } from 'modules/Products';
import { Reminders } from 'modules/Reminders';
import { Reports } from 'modules/Reports';
import SettingsChunk from 'modules/Settings';
import { CustomTasksList, TaskDetail } from 'modules/Tasks';
import { ThirdPartyCase, ThirdPartyCasesIndex } from 'modules/ThirdpartyCases';
import { Upsell } from 'modules/Upsell/components';
import qs from 'query-string';
import { useLocation, useRouteMatch } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import {
  COMPANIES_ROUTE,
  GROUP_CLIENTS_ROUTE,
  GROUP_COMPANIES_ROUTE,
  GROUP_MEMBERS_ROUTE,
  GROUP_ROUTE,
} from 'shared/constants';
import { success } from 'shared/notifications';
import { useCanUseManualActions } from 'shared/utils/connector';
import { Analytics } from '../../modules/Analytics';
import { AcceptTermsOfService } from './AcceptTermsOfService';
import { Client, ClientsImportedDetail, ClientsList, ClientsToConfirmList } from './Clients';
import { ContactPersonForm } from './Clients/Detail/InfoTab/components/ContactPersons/components';
import { DuplicatesClientDetail } from './Clients/Duplicates/DuplicatesClientDetail';
import { DuplicatesClients } from './Clients/Duplicates/DuplicatesClients';
import { ClientForm } from './Clients/Form/ClientForm';
import ManageCreditNote from './CreditNotes/Manage/Manage';
import { GroupGate, GroupOverviewFlagGate } from './GroupGate';
import DraftInvoice from './Invoices/DraftInvoice';
import ImportedInvoiceDetail from './Invoices/ImportedInvoiceDetail';
import InvoiceListToConfirm from './Invoices/InvoiceListToConfirm';
import ManageInvoice from './Invoices/Manage/Manage';
import {
  CustomPaymentPlansList,
  PaymentPlanEditForm as EditPaymentPlan,
  PaymentPlanDetail,
} from './PaymentPlans';
import { PaymentPlanWizard } from './PaymentPlans/PaymentPlanWizard';

const RedirectInvoiceActivity = () => {
  const {
    params: { id, activityId },
    path,
  } = useRouteMatch<{ id: string; activityId: string }>();

  return (
    <Redirect
      to={`/${
        path.includes('invoices') ? 'invoices' : 'credit-notes'
      }/${id}?activityId=${activityId}`}
    />
  );
};

export const PrivateRoutes = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const company = useCompanyConfiguration();
  const hasManualActionsActive = useCanUseManualActions();

  const canUsePaymentPlans = company.package.can_use_payment_plans;
  const canUseExternalMails = company.package.can_use_external_mails;
  const canUseAnalytics = company.package.can_use_analytics;
  const canUseReports = company.package.can_use_reports;

  return (
    <Switch>
      {/* MARK: Dashboard */}
      <Route path="/dashboard" component={Dashboard} />
      <Route path={COMPANIES_ROUTE}>
        <GroupOverviewFlagGate>
          <GroupOverview />
        </GroupOverviewFlagGate>
      </Route>
      <Route path={GROUP_ROUTE}>
        <GroupGate>
          <Switch>
            <Route path={GROUP_CLIENTS_ROUTE} component={GroupClients} />
            <Route path={GROUP_COMPANIES_ROUTE} component={GroupCompanies} />
            <Route path={GROUP_MEMBERS_ROUTE} component={GroupMembers} />
            <Route path={GROUP_ROUTE} component={GroupView} />
          </Switch>
        </GroupGate>
      </Route>

      {/* MARK: Kanban */}
      <Route path="/kanban" component={Kanban} />

      {/* MARK: Import */}
      <Route path="/import" component={Import} />

      {/* MARK: At Risk */}
      <Route path="/at-risk" component={AtRisk} />

      {/* MARK: Client */}
      <Route exact path="/clients" component={ClientsList} />
      <Route path="/clients/new" component={ClientForm} />
      <Route path="/clients/duplicates/:id" component={DuplicatesClientDetail} />
      <Route path="/clients/duplicates" component={DuplicatesClients} />
      <Route path="/clients/to-confirm/:id" component={ClientsImportedDetail} />
      <Route path="/clients/to-confirm" component={ClientsToConfirmList} />
      <Route path="/clients/:id/contact-persons/new" component={ContactPersonForm} />
      <Route
        path="/clients/:id/contact-persons/:contactPersonId/edit"
        component={ContactPersonForm}
      />
      <Route exact path="/clients/:id" component={Client} />
      <Route path="/clients/:id/edit" component={ClientForm} />
      <Route exact path="/clients/:id" component={Client} />

      {/* MARK: Invoices */}
      <Route exact path="/invoices" component={InvoicesListing} />
      <Route path="/invoices/new" component={ManageInvoice} />
      <Route path="/invoices/to-handle" component={InvoicesToHandle} />
      <Route path="/invoices/draft/:id" component={DraftInvoice} />
      <Route path="/invoices/to-confirm/:id" component={ImportedInvoiceDetail} />
      <Route path="/invoices/to-confirm" component={InvoiceListToConfirm} />
      <Route path="/invoices/:id/edit" component={ManageInvoice} />
      <Route path="/invoices/:id/:activityId" component={RedirectInvoiceActivity} />
      <Route path="/invoices/:id" component={Invoice} />

      {/* MARK: Credit notes */}
      <Route exact path="/credit-notes" component={CreditNotesListing} />
      <Route path="/credit-notes/new" component={ManageCreditNote} />
      <Route path="/credit-notes/draft/:id" render={() => <DraftInvoice isCreditNote />} />
      <Route path="/credit-notes/:id/edit" component={ManageCreditNote} />
      <Route path="/credit-notes/:id/:activityId" component={RedirectInvoiceActivity} />
      <Route path="/credit-notes/:id" render={() => <Invoice isCreditNote />} />

      {/* MARK: Others */}
      <Route path="/reminders" component={Reminders} />
      {/* <Route path="/ai-calls" component={AiCallsIndex} />
      <Route path="/ai-calls/:id" component={AiCall} /> */}
      <Route path="/activities" component={Activities} />
      <Route path="/payments/reconciliation" component={Reconciliation} />
      <Route path="/payments/history" component={TransactionHistory} />
      {hasManualActionsActive && <Route path="/products" component={ProductsListing} />}
      <Route path="/thirdparty-cases/:id" component={ThirdPartyCase} />
      <Route path="/thirdparty-cases" component={ThirdPartyCasesIndex} />

      {/* MARK: Payment plans */}
      {canUsePaymentPlans && <Route path="/payment-plans/new" component={PaymentPlanWizard} />}
      {canUsePaymentPlans && <Route path="/payment-plans/:id/edit" component={EditPaymentPlan} />}
      {canUsePaymentPlans && <Route path="/payment-plans/:id" component={PaymentPlanDetail} />}
      {canUsePaymentPlans && <Route path="/payment-plans" component={CustomPaymentPlansList} />}

      {/* MARK: External mailbox */}
      {canUseExternalMails && <Route path="/mails/inbox" component={MailPage} />}
      {canUseExternalMails && <Route path="/mails/outbox" component={MailPage} />}
      {canUseExternalMails && <Route path="/mails/drafts" component={MailPage} />}
      {canUseExternalMails && <Route path="/mails/:id" component={MailDetail} />}
      {canUseExternalMails && <Redirect from="/mails" to="/mails/inbox" />}

      {/* MARK: Tasks */}
      <Route path="/tasks/:id" component={TaskDetail} />
      <Route path="/tasks" component={CustomTasksList} />

      <Route path="/needs-handling/handle-clients" component={AtRisk} />
      <Route path="/needs-handling/handle-invoices" component={InvoicesToHandle} />

      {/* MARK: Analytics */}
      {canUseAnalytics && <Route path="/analytics" component={Analytics} />}
      {canUseReports && <Route path="/reports" component={Reports} />}

      {/* MARK: Upgrade */}
      <Route path="/upgrade/:featureName" component={Upsell} />

      {/* MARK: Settings */}
      <Route path="/settings" component={SettingsChunk} />

      {/* MARK: Terms and Conditions */}
      <Route path="/terms-and-conditions" component={AcceptTermsOfService} />

      {/* MARK: Integrations callback */}
      <Route
        path="/callback"
        render={() => {
          const queryParams = qs.parse(location.search);
          const integration = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);

          if (JSON.parse((queryParams.successfulConnection as any) || false)) {
            success({
              data: t(i18nKeys.FORM.SUCCESSFULLY_CONNEXION, { text: upperFirst(integration) }),
            });
          }

          return (
            <Redirect
              to={`/settings/integrations/${integration}?${
                queryParams.errorMessage ? `errorMessage=${queryParams.errorMessage}` : ''
              }`}
            />
          );
        }}
      />
      <Redirect to="/dashboard" />
    </Switch>
  );
};
